import {ActivatedRouteSnapshot, ResolveFn, Router} from '@angular/router';
import {EnquirusVaultResult} from '@ccp/interfaces/enquirus/enquirus-api-response';
import {Observable, of, switchMap} from 'rxjs';
import {UrlParamLiterals} from '@ccp/enums/literals/urlparams.enum';
import {inject} from '@angular/core';
import {EnquirusService} from '@ccp/services/enquirus/enquirus.service';
import {catchError, take} from 'rxjs/operators';
import {EnquirusVaultRequestStatus, EnquirusVaultTimepieceStatus} from '@ccp/enums/enquirus/enquirus-vault-request-status';
import {EnquirusTimepieceStatus} from '@ccp/enums/enquirus/enquirus.enum';
import {map} from 'rxjs/internal/operators/map';
import {ProductModel} from '@ccp/models/product.model';

export const EnquirusAddTimepieceResolver: ResolveFn<EnquirusVaultResult | boolean> = (
  route: ActivatedRouteSnapshot,
): Observable<EnquirusVaultResult | boolean> => {
  const router = inject(Router);
  const enquirusService = inject(EnquirusService);

  const withProduct = route.queryParams[UrlParamLiterals.isEnquirusWithProduct] === 'true';

  const navigation = router.getCurrentNavigation();
  const state = navigation?.extras?.state as {product: ProductModel};

  if (!withProduct || !!!state?.product) return of(true);

  const timepiece = enquirusService.buildEnquirusTimepieceInput(state.product);

  return enquirusService.checkTimePiece(timepiece).pipe(
    take(1),
    switchMap((response: boolean) => {
      if (response) {
        return of({
          piecesAdded: [enquirusService.mapProductToTimepiece(state.product, EnquirusTimepieceStatus.VALID_ADD_VAULT)],
          piecesNotAdded: [],
          requestStatus: EnquirusVaultRequestStatus.SUCCESS,
          requestOrigin: EnquirusVaultTimepieceStatus.ALREADY_ADDED,
        });
      }

      return enquirusService.addTimePieces([timepiece]).pipe(
        map((response) => {
          if (response.piecesAdded && response.piecesAdded.length > 0) {
            return {
              piecesAdded: [enquirusService.mapProductToTimepiece(state.product, EnquirusTimepieceStatus.VALID_ADD_VAULT)],
              piecesNotAdded: [],
              requestStatus: EnquirusVaultRequestStatus.SUCCESS,
              requestOrigin: EnquirusVaultTimepieceStatus.TO_ADD,
            };
          }
          return {
            piecesAdded: [],
            piecesNotAdded: [enquirusService.mapProductToTimepiece(state.product, EnquirusTimepieceStatus.INVALID_ADD_VAULT)],
            requestStatus: EnquirusVaultRequestStatus.FAILED,
            requestOrigin: EnquirusVaultTimepieceStatus.TO_ADD,
          };
        }),
        catchError(() => {
          console.error('Error adding timepiece, falling back to default response');
          return of(true);
        }),
      );
    }),
    catchError(() => {
      console.error('Error checking timepiece, falling back to default response');
      return of(true);
    }),
  );
};
